import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  Plus,
  MagnifyingGlass,
  FunnelSimple,
  CircleNotch,
  Download,
  PaperPlaneTilt,
  Trash
} from '@phosphor-icons/react';


function QuoteStatusBadge({ status }) {
  const styles = {
    'Draft': 'badge-draft',
    'Pending Approval': 'badge-pending',
    'Approved': 'badge-approved',
    'Sent': 'badge-sent',
    'Signed': 'badge-signed',
    'Paid': 'badge-paid',
    'Expired': 'badge-expired',
  };

  const labels = {
    'Draft': 'Borrador',
    'Pending Approval': 'Pendiente Aprobación',
    'Approved': 'Aprobada',
    'Sent': 'Enviada',
    'Signed': 'Firmada',
    'Paid': 'Pagada',
    'Expired': 'Vencida',
  };

  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${styles[status] || 'badge-draft'}`}>
      {labels[status] || status}
    </span>
  );
}

export default function QuotesPage() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    fetchQuotes();
  }, [statusFilter]);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API_URL}/api/quotes${params}`, {
        withCredentials: true
      });
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (e, quoteId) => {
    e.stopPropagation();
    try {
      const response = await axios.get(`${API_URL}/api/quotes/${quoteId}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cotizacion-${quoteId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const handleDeleteQuote = async (e, quoteId) => {
    e.stopPropagation();
    if (!window.confirm('Eliminar esta cotizacion?')) return;
    try {
      await axios.delete(`${API_URL}/api/quotes/${quoteId}`, { withCredentials: true });
      fetchQuotes();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al eliminar cotizacion');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleAll = () => {
    setSelectedIds(prev => prev.size === quotes.length ? new Set() : new Set(quotes.map(q => q.id)));
  };
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Eliminar ${selectedIds.size} cotizaciones seleccionadas?`)) return;
    try {
      await axios.post(`${API_URL}/api/bulk/delete-quotes`, { ids: [...selectedIds] }, { withCredentials: true });
      setSelectedIds(new Set());
      fetchQuotes();
    } catch (e) { alert('Error al eliminar'); }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="quotes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Cotizaciones</h1>
          <p className="text-slate-500">Gestiona tus cotizaciones y estimados</p>
        </div>
        <div className="flex gap-2">
          {selectedIds.size > 0 && (
            <button onClick={handleBulkDelete} className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium" data-testid="bulk-delete-quotes">
              <Trash size={18} /> Eliminar ({selectedIds.size})
            </button>
          )}
          <Link to="/quotes/new" className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" data-testid="new-quote-button">
            <Plus size={20} weight="bold" /> Nueva Cotizacion
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-4">
          <FunnelSimple size={20} className="text-slate-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            data-testid="status-filter"
          >
            <option value="">Todos los estados</option>
            <option value="Draft">Borrador</option>
            <option value="Pending Approval">Pendiente Aprobación</option>
            <option value="Approved">Aprobada</option>
            <option value="Sent">Enviada</option>
            <option value="Signed">Firmada</option>
            <option value="Paid">Pagada</option>
            <option value="Expired">Vencida</option>
          </select>
        </div>
      </div>

      {/* Quotes List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <CircleNotch size={32} className="animate-spin text-blue-600" />
          </div>
        ) : quotes.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="quotes-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-4 w-10">
                    <input type="checkbox" checked={selectedIds.size === quotes.length && quotes.length > 0} onChange={toggleAll} className="w-4 h-4 accent-blue-600" data-testid="select-all-quotes" />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Numero
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Cliente
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Fecha
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Total
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Estado
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-500">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {quotes.map((quote) => (
                  <tr
                    key={quote.id}
                    onClick={() => navigate(`/quotes/${quote.id}`)}
                    className={`table-row-hover cursor-pointer ${selectedIds.has(quote.id) ? 'bg-blue-50' : ''}`}
                    data-testid={`quote-row-${quote.id}`}
                  >
                    <td className="px-3 py-4 w-10">
                      <input type="checkbox" checked={selectedIds.has(quote.id)} onChange={() => toggleSelect(quote.id)} onClick={e => e.stopPropagation()} className="w-4 h-4 accent-blue-600" />
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-slate-900">{quote.quote_number}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-slate-900">{quote.client_name}</p>
                        <p className="text-sm text-slate-500">{quote.client_email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {new Date(quote.created_at).toLocaleDateString('es-ES')}
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-slate-900">
                        ${quote.total?.toLocaleString()}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <QuoteStatusBadge status={quote.status} />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => handleDownloadPdf(e, quote.id)}
                          className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-600"
                          title="Descargar PDF"
                          data-testid={`download-pdf-${quote.id}`}
                        >
                          <Download size={18} />
                        </button>
                        <button
                          onClick={(e) => handleDeleteQuote(e, quote.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors text-slate-400 hover:text-red-600"
                          title="Eliminar cotizacion"
                          data-testid={`delete-quote-${quote.id}`}
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-16 text-center">
            <p className="text-slate-500 mb-4">No se encontraron cotizaciones</p>
            <Link
              to="/quotes/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus size={18} />
              Crear primera cotización
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
